import React from 'react'
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { FiClock, FiTrendingUp, FiTag, FiGrid } from 'react-icons/fi';

function FootProducts({ latest, hotProducts, brands, latestCategories }) {
  const weburl = useSelector(state => state.constants.weburl);

  const sections = [
    {
      title: 'Latest Arrivals',
      icon: <FiClock className="text-blue-500" size={18} />,
      data: latest,
      type: 'product',
      linkPath: (id) => `/products/${id}`
    },
    {
      title: 'Hot Items',
      icon: <FiTrendingUp className="text-orange-500" size={18} />,
      data: hotProducts,
      type: 'product',
      linkPath: (id) => `/products/${id}`
    },
    {
      title: 'Popular Brands',
      icon: <FiTag className="text-green-500" size={18} />,
      data: brands,
      type: 'brand',
      linkPath: (id) => `/products/${id}`
    },
    {
      title: 'Categories',
      icon: <FiGrid className="text-purple-500" size={18} />,
      data: latestCategories,
      type: 'category',
      linkPath: (id) => `/products/${id}`
    }
  ];

  const ProductItem = ({ item }) => (
    <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 transition-all duration-200 group">
      <div className="w-14 h-14 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden">
        <img 
          src={`${weburl}/${item.image}`} 
          alt={item.name} 
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      </div>
      <div className="flex-1 min-w-0">
        {item.category_name && (
          <p className="text-xs text-gray-400 truncate">{item.category_name}</p>
        )}
        <h4 className="text-xs font-medium text-gray-800 truncate group-hover:text-blue-600 transition-colors">
          {item.name}
        </h4>
        {item.unitretailprice && (
          <p className="text-xs font-semibold text-red-500">Rs. {item.unitretailprice}</p>
        )}
      </div>
    </div>
  );

  const BrandItem = ({ item }) => (
    <div className="flex flex-col items-center p-3 rounded-lg hover:bg-gray-50 transition-all duration-200 group">
      <div className="w-16 h-16 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden mb-2">
        <img 
          src={`${weburl}/${item.image}`} 
          alt={item.name} 
          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
        />
      </div>
      <div className="text-center">
        <h4 className="text-xs font-medium text-gray-800 group-hover:text-blue-600 transition-colors line-clamp-2">
          {item.name}
        </h4>
      </div>
    </div>
  );

  const CategoryItem = ({ item }) => (
    <div className="flex flex-col items-center p-3 rounded-lg hover:bg-gray-50 transition-all duration-200 group">
      <div className="w-16 h-16 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden mb-2">
        <img 
          src={`${weburl}/${item.image}`} 
          alt={item.name} 
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      </div>
      <div className="text-center">
        <h4 className="text-xs font-medium text-gray-800 group-hover:text-blue-600 transition-colors line-clamp-2">
          {item.name}
        </h4>
      </div>
    </div>
  );

  const renderItem = (item, type) => {
    switch (type) {
      case 'product':
        return <ProductItem item={item} />;
      case 'brand':
        return <BrandItem item={item} />;
      case 'category':
        return <CategoryItem item={item} />;
      default:
        return null;
    }
  };

  return (
    <div className="bg-white py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {sections.map((section, idx) => (
            <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-300">
              {/* Section Header */}
              <div className="px-3 sm:px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-gray-100">
                    {section.icon}
                  </div>
                  <h3 className="font-semibold text-gray-800 text-sm sm:text-base">
                    {section.title}
                  </h3>
                </div>
              </div>

              {/* Section Content - Grid layout for brands/categories on mobile */}
              {section.type === 'product' ? (
                <div className="divide-y divide-gray-100">
                  {section.data.slice(0, 4).map((item, index) => (
                    <Link 
                      to={section.linkPath(item.id)} 
                      key={index}
                      className="block hover:no-underline"
                    >
                      {renderItem(item, section.type)}
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-1 p-2">
                  {section.data.slice(0, 4).map((item, index) => (
                    <Link 
                      to={section.linkPath(item.id)} 
                      key={index}
                      className="block hover:no-underline"
                    >
                      {renderItem(item, section.type)}
                    </Link>
                  ))}
                </div>
              )}

              {/* View All Link */}
              {section.data.length > 4 && (
                <div className="px-3 sm:px-4 py-2 border-t border-gray-100 bg-gray-50">
                  <Link 
                    to={`/${section.type === 'product' ? 'products' : section.type === 'brand' ? 'brands' : 'categories'}`}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center justify-center gap-1"
                  >
                    View All
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default FootProducts;